import { memo, useMemo, useState } from 'react'
import { ChartSubType, ChartType, chartTypeToJSON, chartSubTypeToJSON } from '../../../../proto/generated/chart'
import { IndicatorOutput_OutputStyle } from '../../../../proto/generated/indicator'
import { ExtraSmallButton } from '../../../custom'
import { ListItemIcon, ListItemText, MenuItem, Popover, Typography } from '@mui/material'
import { PlotTypesMenu } from '../../../../assets/constants'
import { PlotTypesMenuItemType } from '../../chart.typings'
import Line from '../../../../assets/icons/chartTypes/line'
import CurvedLine from '../../../../assets/icons/plotTypes/curvedLine'
import StepLine from '../../../../assets/icons/plotTypes/stepLine'
import Column from '../../../../assets/icons/plotTypes/columns'
import Area from '../../../../assets/icons/plotTypes/area'
import Cross from '../../../../assets/icons/plotTypes/cross'
import Circles from '../../../../assets/icons/plotTypes/circles'
import StepLineWithDiamonds from '../../../../assets/icons/plotTypes/stepLineWithDiamonds'
import LineWithBreaks from '../../../../assets/icons/plotTypes/lineWithBreaks'
import StepLineWithBreaks from '../../../../assets/icons/plotTypes/stepLineWithBreaks'
import Histogram from '../../../../assets/icons/plotTypes/histogram'
import AnalyticsManager from '../../../../analytics/AnalyticsManager'
import { indicatorOutput_OutputStyleToJSON } from '../../../../proto/generated/indicator'

type Props = {
    outputChartType: ChartType,
    outputSubType: ChartSubType,
    outputStyle: IndicatorOutput_OutputStyle,
    onSelected: (menuItem: PlotTypesMenuItemType) => void
}

export const getPlotTypeIcon = (type: ChartType,
                                subType: ChartSubType,
                                style: IndicatorOutput_OutputStyle) => {
    if (type === ChartType.LINE && subType === ChartSubType.SIMPLE && style === IndicatorOutput_OutputStyle.SOLID) {
        return <Line />
    }
    if (type === ChartType.LINE && subType === ChartSubType.CURVED_LINE && style === IndicatorOutput_OutputStyle.SOLID) {
        return <CurvedLine />
    }
    if (type === ChartType.LINE && subType === ChartSubType.STEP_LINE && style === IndicatorOutput_OutputStyle.SOLID) {
        return <StepLine />
    }
    if (type === ChartType.COLUMN && subType === ChartSubType.SIMPLE && style === IndicatorOutput_OutputStyle.SOLID) {
        return <Column />
    }
    if (type === ChartType.HISTOGRAM && subType === ChartSubType.SIMPLE && style === IndicatorOutput_OutputStyle.SOLID) {
        return <Histogram />
    }
    if (type === ChartType.AREA && subType === ChartSubType.SIMPLE && style === IndicatorOutput_OutputStyle.SOLID) {
        return <Area />
    }
    if (type === ChartType.LINE && subType === ChartSubType.CROSS_LINE && style === IndicatorOutput_OutputStyle.SOLID) {
        return <Cross />
    }
    if (type === ChartType.LINE && subType === ChartSubType.CIRCLE_LINE && style === IndicatorOutput_OutputStyle.SOLID) {
        return <Circles />
    }
    if (type === ChartType.LINE && subType === ChartSubType.STEP_LINE && style === IndicatorOutput_OutputStyle.WITH_DIAMONDS) {
        return <StepLineWithDiamonds />
    }
    if (type === ChartType.LINE && subType === ChartSubType.SIMPLE && style === IndicatorOutput_OutputStyle.WITH_BREAKS) {
        return <LineWithBreaks />
    }
    if (type === ChartType.LINE && subType === ChartSubType.STEP_LINE && style === IndicatorOutput_OutputStyle.WITH_BREAKS) {
        return <StepLineWithBreaks />
    }
    return null
}

const SeriesOutputPlotTypesView = ({
                                       outputChartType = ChartType.CHART_TYPE_UNKNOWN,
                                       outputStyle = IndicatorOutput_OutputStyle.SOLID,
                                       outputSubType = ChartSubType.SIMPLE,
                                       onSelected
                                   }: Props) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

    const selectedItem = useMemo(() => {
        return PlotTypesMenu.find((menuItem) => (outputChartType === menuItem.type && outputSubType === menuItem.subType && outputStyle === menuItem.style))
    }, [PlotTypesMenu, outputChartType, outputSubType, outputStyle])

    const plotTypeIcon = useMemo(() => getPlotTypeIcon(outputChartType, outputSubType, outputStyle), [outputChartType, outputSubType, outputStyle])

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const onChange = (menuItem: PlotTypesMenuItemType) => {
        if (outputChartType !== menuItem.type || outputSubType !== menuItem.subType || outputStyle !== menuItem.style) {
            onSelected(menuItem)
        }
    }

    return (<>
        <ExtraSmallButton
            onClick={handleClick}
            variant="outlined"
            color="inherit"
            style={{
                padding: '4px',
                height: '28px',
                minHeight: '28px',
                minWidth: '28px',
                width: plotTypeIcon ? '28px' : 'auto',
                boxSizing: 'border-box',
                lineHeight: 1,
            }}
            sx={{
                borderRadius: 1,
                height: '28px !important',
                minHeight: '28px !important',
                minWidth: '28px !important',
                '& svg': {
                    width: 20,
                    height: 20,
                    display: 'block',
                },
            }}
        >
            {plotTypeIcon ? plotTypeIcon : <Typography sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                px: 0.5
            }}> {selectedItem?.label ?? ''}</Typography>}
        </ExtraSmallButton>
        <Popover
            elevation={3}
            open={Boolean(anchorEl)}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
            }}
            anchorEl={anchorEl}
            sx={{ overflowY: 'auto' }}
            onClose={() => setAnchorEl(null)}
        >
            {
                PlotTypesMenu.map((menuItem) => {
                    return menuItem.disabled ? null : (<MenuItem
                        selected={(outputChartType === menuItem.type && outputSubType === menuItem.subType && outputStyle === menuItem.style)}
                        key={menuItem.label} onClick={() => {
                        AnalyticsManager.logEvent('Indicator plot type changed', {
                            chartType: chartTypeToJSON(menuItem.type),
                            chartSubType: chartSubTypeToJSON(menuItem.subType),
                            style: indicatorOutput_OutputStyleToJSON(menuItem.style)
                        })
                        onChange(menuItem)
                        setAnchorEl(null)
                    }}>
                        <ListItemIcon>
                            {getPlotTypeIcon(menuItem.type, menuItem.subType, menuItem.style)}
                        </ListItemIcon>
                        <ListItemText>{menuItem.label}</ListItemText>
                    </MenuItem>)
                })
            }
        </Popover>
    </>)
}

export default memo(SeriesOutputPlotTypesView)